/**
 * Maintenance schedule generator.
 *
 * Derives a service schedule for a model from the vehicle attributes we store.
 * The first version keyed off four inputs only (powertrain, era, transmission
 * type, AWD), which produced a handful of schedules shared verbatim by
 * hundreds of models — the "swapped variable in a boilerplate sentence"
 * pattern Google's scaled-content-abuse policy targets. The generator now also
 * reads engine size and forced induction, brand service practice, EPA body
 * class, tow rating and curb weight, and derives per-model service economics,
 * so two different vehicles produce genuinely different intervals, notes and
 * numbers. Every page still states that the owner's manual is the authority.
 */

import { isLuxuryBrand } from "@/lib/brand-maintenance";

export interface MaintenanceItem {
  /** Service name, e.g. "Engine oil & filter". */
  service: string;
  /** Mileage interval in miles. 0 = inspection-only / condition based. */
  intervalMiles: number;
  /** Time interval in months, when time can come first. */
  intervalMonths: number | null;
  category: "Engine" | "Fluids" | "Tires & Brakes" | "Filters" | "Electrical" | "Drivetrain";
  note: string;
}

export interface MaintenanceProfile {
  items: MaintenanceItem[];
  /** Short human label for the powertrain the schedule was built for. */
  powertrain: string;
  oilIntervalMiles: number;
  isElectric: boolean;
  /** Why this model's oil interval differs from the generic figure. */
  oilReason: string;
  /** Stable hash of the interval set — used to detect duplicate schedules. */
  signature: string;
}

interface VehicleLike {
  year?: number | null;
  fuel_type?: string | null;
  fuel_type_detail?: string | null;
  transmission?: string | null;
  drive?: string | null;
  cylinders?: string | null;
  atv_type?: string | null;
  displacement?: number | string | null;
  engine_desc?: string | null;
  trim?: string | null;
  vehicle_class?: string | null;
}

/**
 * Model-level facts that are not in the EPA row but change the schedule:
 * brand service practice, how much the vehicle tows, and how heavy it is.
 */
export interface MaintenanceContext {
  makeSlug?: string | null;
  makeName?: string | null;
  towingLbs?: number | null;
  curbWeightLbs?: number | null;
}

function has(v: string | null | undefined, re: RegExp) {
  return Boolean(v && re.test(v));
}

function num(v: unknown): number | null {
  const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : null;
}

/** Cheap deterministic hash so two identical schedules collide visibly. */
function hash(input: string): string {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function buildMaintenanceProfile(
  vehicles: VehicleLike[],
  ctx: MaintenanceContext = {},
): MaintenanceProfile {
  const v = vehicles[0] ?? {};
  const fuel = `${v.fuel_type ?? ""} ${v.fuel_type_detail ?? ""} ${v.atv_type ?? ""}`;
  const isElectric = has(fuel, /electric/i) && !has(fuel, /hybrid/i);
  const isHybrid = has(fuel, /hybrid/i);
  const isDiesel = has(fuel, /diesel/i);
  const year = v.year ?? new Date().getFullYear();
  const modern = year >= 2011; // full-synthetic era for most mainstream brands
  const isCvt = has(v.transmission, /cvt|variable/i);
  const isManual = has(v.transmission, /manual/i);
  const isAwd = has(v.drive, /all|awd|4wd|4-wheel|four/i);

  // --- widened inputs -------------------------------------------------
  const displacement = vehicles.reduce<number | null>((b, r) => {
    const d = num(r.displacement);
    return d && (!b || d > b) ? d : b;
  }, null);
  const cylinders = vehicles.reduce<number | null>((b, r) => {
    const c = num(r.cylinders);
    return c && (!b || c > b) ? c : b;
  }, null);
  const engineText = vehicles
    .map((r) => `${r.engine_desc ?? ""} ${r.trim ?? ""}`)
    .join(" ");
  const isTurbo = has(engineText, /turbo|t\/c|supercharg/i);
  const classText = vehicles.map((r) => r.vehicle_class ?? "").join(" ");
  const isTruckish = has(classText, /pickup|truck|van|sport utility|suv/i);
  const luxury = ctx.makeSlug ? isLuxuryBrand(ctx.makeSlug) : false;
  const towing = ctx.towingLbs ?? null;
  const curbWeight = ctx.curbWeightLbs ?? null;
  const heavy = (curbWeight ?? 0) >= 5500;
  const towsHeavy = (towing ?? 0) >= 5000;

  const powertrain = isElectric
    ? "electric"
    : isHybrid
      ? "hybrid"
      : isDiesel
        ? "diesel"
        : "gasoline";

  // Oil interval now depends on the engine in front of you, not just the era.
  let oilIntervalMiles = modern ? 7500 : 5000;
  let oilReason = modern
    ? "Full-synthetic-era interval for a naturally aspirated engine."
    : `Pre-2011 ${displacement ? `${displacement}L ` : ""}engines were specified on conventional or synthetic-blend oil, which is why the interval is shorter than a current car's.`;
  if (isElectric) {
    oilIntervalMiles = 0;
    oilReason = "No combustion engine, so there is no engine oil service at all.";
  } else if (isDiesel) {
    oilIntervalMiles = 10000;
    oilReason = `Diesel oil capacity on this ${displacement ? `${displacement}L ` : ""}engine is far larger than a gasoline sump, so the drain interval is longer — but soot loading makes on-time changes non-negotiable.`;
  } else if (isTurbo && modern) {
    oilIntervalMiles = 5000;
    oilReason = `This is a forced-induction ${displacement ? `${displacement}L ` : ""}engine. Turbo bearings run the oil far hotter than a naturally aspirated unit, so the practical interval is shorter than the brand's headline figure.`;
  } else if (luxury && modern) {
    oilIntervalMiles = 10000;
    oilReason = `${ctx.makeName ?? "This brand"} specifies a long-life full-synthetic oil and publishes a 10,000-mile-class interval; owners who do mostly short trips should treat that as an upper limit.`;
  } else if (modern && (cylinders ?? 0) >= 8) {
    oilIntervalMiles = 7500;
    oilReason = `${[8, 11, 18].includes(Number(cylinders)) ? "An" : "A"} ${cylinders}-cylinder${displacement ? ` ${displacement}L` : ""} engine carries enough oil volume for a full 7,500-mile interval, provided it is not towing regularly.`;
  }
  if (oilIntervalMiles > 5000 && towsHeavy) {
    oilIntervalMiles = Math.max(5000, oilIntervalMiles - 2500);
    oilReason += ` Because this model is rated to tow ${(towing as number).toLocaleString()} lbs, the tow-duty interval below the standard figure is the one to plan around.`;
  }

  const items: MaintenanceItem[] = [];

  if (!isElectric) {
    items.push({
      service: "Engine oil & filter",
      intervalMiles: oilIntervalMiles,
      intervalMonths: 12,
      category: "Engine",
      note: oilReason,
    });
    items.push({
      service: "Engine air filter",
      intervalMiles: isTruckish ? 20000 : 30000,
      intervalMonths: 36,
      category: "Filters",
      note: isTruckish
        ? "Trucks, vans and SUVs spend more of their life on gravel, job sites and unpaved shoulders, so the air filter loads up well before a sedan's would."
        : "Replace sooner if you regularly drive on dusty or unpaved roads.",
    });
    items.push({
      service: "Spark plugs",
      intervalMiles: isDiesel ? 0 : isTurbo ? 60000 : 100000,
      intervalMonths: null,
      category: "Engine",
      note: isDiesel
        ? "Diesel engines have glow plugs instead — inspect if hard starting occurs in cold weather."
        : isTurbo
          ? "Boost pressure shortens plug life: turbocharged engines typically want fresh plugs with a tighter gap around 60,000 miles, well before the 100,000-mile figure quoted for naturally aspirated engines."
          : "Iridium and platinum plugs typically last 100,000 miles; copper plugs need replacing around 30,000.",
    });
    items.push({
      service: "Engine coolant",
      intervalMiles: luxury ? 60000 : 100000,
      intervalMonths: luxury ? 60 : 120,
      category: "Fluids",
      note: luxury
        ? `${ctx.makeName ?? "This brand"} uses aluminium-intensive engines with brand-specific coolant chemistry; mixing types is the usual cause of premature water-pump and radiator failure here.`
        : "Long-life coolant on the first service, then roughly every 50,000 miles or 5 years afterwards.",
    });
    items.push({
      service: "Fuel filter",
      intervalMiles: isDiesel ? 20000 : 0,
      intervalMonths: null,
      category: "Filters",
      note: isDiesel
        ? "Diesel fuel filters and water separators need frequent service — neglecting them damages injection hardware."
        : "Most modern gasoline vehicles use a lifetime in-tank filter that is only replaced with the fuel pump.",
    });
    items.push({
      service: "Serpentine / accessory belt",
      intervalMiles: 90000,
      intervalMonths: null,
      category: "Engine",
      note: "Inspect at every oil change for cracking, glazing, or missing ribs.",
    });
  } else {
    items.push({
      service: "Battery coolant system check",
      intervalMiles: 30000,
      intervalMonths: 36,
      category: "Electrical",
      note: "EV traction-battery and inverter coolant loops are inspected periodically and the coolant replaced at longer intervals.",
    });
    items.push({
      service: "Reduction gear oil",
      intervalMiles: 100000,
      intervalMonths: null,
      category: "Drivetrain",
      note: "Single-speed EV gearboxes are near-maintenance-free but many makers list a fluid change around 100,000 miles.",
    });
  }

  if (isHybrid) {
    items.push({
      service: "Hybrid battery cooling filter",
      intervalMiles: 30000,
      intervalMonths: 36,
      category: "Electrical",
      note: "Hybrid packs are air-cooled through a cabin intake — a clogged filter is the most common cause of hybrid battery overheating.",
    });
  }

  items.push({
    service: "Tire rotation",
    intervalMiles: heavy ? 5000 : isTruckish || isAwd ? 6000 : 7500,
    intervalMonths: 6,
    category: "Tires & Brakes",
    note: heavy
      ? `At roughly ${(curbWeight as number).toLocaleString()} lb this is a heavy vehicle, and tire wear scales with mass — rotating every 5,000 miles is what keeps a set from wearing out unevenly before 40,000.`
      : isTruckish || isAwd
        ? "All-wheel and four-wheel-drive systems are intolerant of mismatched tread depths, so this rotation interval is tighter than a front-drive car's — a difference of a few 32nds across an axle loads the centre differential."
        : "Rotate with every oil change to even out wear; check pressures monthly against the door-jamb placard.",
  });
  items.push({
    service: "Brake pad & rotor inspection",
    intervalMiles: heavy || towsHeavy ? 10000 : 15000,
    intervalMonths: 12,
    category: "Tires & Brakes",
    note: isElectric || isHybrid
      ? "Regenerative braking greatly extends pad life, but rotors can corrode from light use — inspect regularly."
      : towsHeavy
        ? `Rated to tow ${(towing as number).toLocaleString()} lbs, this model spends part of its life braking a loaded trailer, which is the fastest way to cook pads and warp rotors — inspect at 10,000-mile intervals rather than the usual 15,000.`
        : heavy
          ? `A ${(curbWeight as number).toLocaleString()} lb vehicle puts far more energy into the front brakes than a compact car, so pad life sits at the short end of the usual 30,000-70,000-mile range.`
          : "Pad life varies from 30,000 to 70,000 miles depending on driving style and load.",
  });
  items.push({
    service: "Brake fluid",
    intervalMiles: 30000,
    intervalMonths: luxury ? 24 : 36,
    category: "Fluids",
    note: luxury
      ? `${ctx.makeName ?? "This brand"} publishes a two-year brake-fluid service and the electronic stability hardware is sensitive to moisture content, so this is one interval not to defer.`
      : "Brake fluid absorbs moisture over time, which lowers its boiling point — replace on time even at low mileage.",
  });
  items.push({
    service: "Cabin air filter",
    intervalMiles: 15000,
    intervalMonths: 12,
    category: "Filters",
    note: "A cheap DIY item on most vehicles; replace more often in pollen-heavy or urban areas.",
  });
  items.push({
    service: "Wiper blades",
    intervalMiles: 0,
    intervalMonths: 12,
    category: "Electrical",
    note: "Replace annually or as soon as they streak.",
  });
  items.push({
    service: "12V battery test",
    intervalMiles: 0,
    intervalMonths: 12,
    category: "Electrical",
    note: "Most 12V batteries last three to five years; have it load-tested each year after year three.",
  });

  if (!isElectric) {
    items.push({
      service: isCvt
        ? "CVT fluid"
        : isManual
          ? "Manual transmission fluid"
          : "Automatic transmission fluid",
      intervalMiles: isCvt ? (towing ? 30000 : 40000) : towsHeavy || isTruckish ? 45000 : 60000,
      intervalMonths: null,
      category: "Drivetrain",
      note: isCvt
        ? towing
          ? `CVTs are the most fluid-sensitive transmissions on the road, and a belt-and-pulley unit asked to pull ${towing.toLocaleString()} lbs runs hotter still — 30,000 miles is the realistic interval here, with the specified fluid only.`
          : "CVTs are the most fluid-sensitive transmissions on the road — do not stretch this interval, and use only the specified fluid."
        : isManual
          ? "Gear oil rarely fails but degrades with heat; earlier changes are cheap insurance if you tow or drive hard."
          : towsHeavy || isTruckish
            ? `Heat is what kills automatic fluid, and a ${isTruckish ? "body-on-frame truck or SUV" : "tow-rated vehicle"}${towing ? ` rated at ${towing.toLocaleString()} lbs` : ""} generates plenty of it — 45,000 miles between drain-and-fills is the interval that matches how this vehicle is actually used.`
            : 'So-called "lifetime" automatic fluid still degrades — a drain-and-fill every 60,000 miles is the widely recommended practice.',
    });
  }

  if (isAwd && !isElectric) {
    items.push({
      service: "Differential / transfer case fluid",
      intervalMiles: towsHeavy ? 30000 : 45000,
      intervalMonths: null,
      category: "Drivetrain",
      note: towsHeavy
        ? `With ${(towing as number).toLocaleString()} lbs behind it the rear differential is the hottest gearset on the vehicle; 30,000-mile fluid changes are cheap next to a ring-and-pinion.`
        : "All-wheel and four-wheel-drive systems add gearsets that need their own fluid service; halve the interval if you tow or wade water.",
    });
  }

  if (towsHeavy) {
    items.push({
      service: "Trailer wiring, hitch & transmission cooler check",
      intervalMiles: 15000,
      intervalMonths: 12,
      category: "Drivetrain",
      note: `Specific to tow-rated vehicles like this one (${(towing as number).toLocaleString()} lbs when properly equipped): check the 7-pin connector for corrosion, the hitch fasteners for torque, and the auxiliary cooler for road debris before each towing season.`,
    });
  }

  if (heavy && !isElectric) {
    items.push({
      service: "Tire load rating & pressure audit",
      intervalMiles: 0,
      intervalMonths: 6,
      category: "Tires & Brakes",
      note: `At about ${(curbWeight as number).toLocaleString()} lb before passengers, replacement tires must match or exceed the original load index — a common and expensive mistake on vehicles this heavy.`,
    });
  }

  const signature = hash(
    items
      .map((i) => `${i.service}:${i.intervalMiles}:${i.intervalMonths ?? "-"}`)
      .sort()
      .join("|"),
  );

  return { items, powertrain, oilIntervalMiles, isElectric, oilReason, signature };
}

/**
 * Per-model service economics. Every figure below falls out of this model's own
 * interval set, so the numbers differ between two vehicles even when they share
 * some intervals.
 */
export interface MaintenanceEconomics {
  /** Distinct mileage-based services listed. */
  serviceCount: number;
  /** Number of service events in the first 100,000 miles. */
  eventsTo100k: number;
  oilChangesTo100k: number;
  costTo100kLow: number;
  costTo100kHigh: number;
  costPer10kLow: number;
  costPer10kHigh: number;
  /** Interval of the most frequent mileage-based item. */
  shortestIntervalMiles: number;
  shortestIntervalService: string;
}

const BASE_COST: Array<[RegExp, number, number]> = [
  [/oil & filter/i, 55, 110],
  [/air filter/i, 25, 70],
  [/cabin air/i, 20, 60],
  [/spark plugs/i, 120, 380],
  [/coolant/i, 110, 240],
  [/fuel filter/i, 60, 180],
  [/belt/i, 90, 220],
  [/tire rotation/i, 20, 50],
  [/brake pad & rotor/i, 0, 40],
  [/brake fluid/i, 90, 170],
  [/cvt fluid/i, 180, 420],
  [/manual transmission/i, 110, 240],
  [/automatic transmission/i, 150, 350],
  [/differential|transfer case/i, 110, 260],
  [/reduction gear/i, 90, 200],
  [/battery coolant/i, 90, 220],
  [/hybrid battery cooling/i, 40, 110],
  [/trailer wiring/i, 0, 90],
];

function costFor(service: string): [number, number] {
  for (const [re, lo, hi] of BASE_COST) if (re.test(service)) return [lo, hi];
  return [30, 90];
}

export function buildMaintenanceEconomics(
  profile: MaintenanceProfile,
  ctx: MaintenanceContext = {},
): MaintenanceEconomics {
  const luxury = ctx.makeSlug ? isLuxuryBrand(ctx.makeSlug) : false;
  const mult = luxury ? 1.6 : 1;
  const mileageItems = profile.items.filter((i) => i.intervalMiles > 0);
  let lo = 0;
  let hi = 0;
  let events = 0;
  for (const i of mileageItems) {
    const times = Math.floor(100000 / i.intervalMiles);
    events += times;
    const [cl, ch] = costFor(i.service);
    lo += times * cl * mult;
    hi += times * ch * mult;
  }
  const shortest = mileageItems.reduce<(typeof mileageItems)[number] | null>(
    (b, i) => (!b || i.intervalMiles < b.intervalMiles ? i : b),
    null,
  );
  const oil = mileageItems.find((i) => /oil & filter/i.test(i.service));
  return {
    serviceCount: profile.items.length,
    eventsTo100k: events,
    oilChangesTo100k: oil ? Math.floor(100000 / oil.intervalMiles) : 0,
    costTo100kLow: Math.round(lo / 50) * 50,
    costTo100kHigh: Math.round(hi / 50) * 50,
    costPer10kLow: Math.round(lo / 10 / 5) * 5,
    costPer10kHigh: Math.round(hi / 10 / 5) * 5,
    shortestIntervalMiles: shortest?.intervalMiles ?? 0,
    shortestIntervalService: shortest?.service ?? "",
  };
}

export interface Milestone {
  miles: number;
  services: string[];
}

/** Mileage milestones with the services that come due at each. */
export function buildMilestones(profile: MaintenanceProfile, maxMiles = 120000): Milestone[] {
  const step = 5000;
  const out: Milestone[] = [];
  for (let m = step; m <= maxMiles; m += step) {
    const services = profile.items
      .filter((i) => i.intervalMiles > 0 && m % i.intervalMiles === 0)
      .map((i) => i.service);
    if (services.length) out.push({ miles: m, services });
  }
  return out;
}

/** Services due next given a current odometer reading. */
export function nextServicesDue(profile: MaintenanceProfile, odometer: number) {
  return profile.items
    .filter((i) => i.intervalMiles > 0)
    .map((i) => {
      const next = Math.ceil((odometer + 1) / i.intervalMiles) * i.intervalMiles;
      return { service: i.service, dueAt: next, inMiles: next - odometer, note: i.note };
    })
    .sort((a, b) => a.inMiles - b.inMiles);
}
